import { TTechnologyNameTitle } from '@interfaces/common/common';
import {
	IDefaultOptions,
	IGroupSelectCategoryGroup,
	IGroupSelectItem,
	IGroupSelectOptions,
	IGroupSelectOptionsKpi,
} from './GroupSelect.types';

export const generateOptions = (options: Array<IGroupSelectOptions>, checked: boolean) => {
	return options.map((option: IGroupSelectOptions) => {
		return {
			...option,
			categoryGroups: option.categoryGroups.map((categoryGroup: IGroupSelectCategoryGroup) => {
				return {
					...categoryGroup,
					items: categoryGroup.items.map((item: IGroupSelectItem) => {
						return {
							...item,
							checked,
						};
					}),
				};
			}),
		};
	});
};

export const generateOptionsByIds = (
	options: Array<IGroupSelectOptions>,
	isChecked: boolean,
	ids: Array<string>,
	categoriesNames: string[],
	technology?: TTechnologyNameTitle,
): IGroupSelectOptions[] => {
	return options.map((option: IGroupSelectOptions) => {
		if (technology && option.technology.name !== technology.name) return option;

		return {
			...option,
			categoryGroups: option.categoryGroups.map((categoryGroup: IGroupSelectCategoryGroup) => {
				return {
					...categoryGroup,
					items: categoryGroup.items.map((item: IGroupSelectItem) => {
						const checked = ids.includes(item.id)
							? { checked: isChecked }
							: {};

						return {
							...item,
							...checked,
						};
					}),
				};
			}),
		};
	});
};

export const generateListOfCheckedOptions = (
	options: Array<IGroupSelectOptions>,
): IGroupSelectItem[] => {
	return options.reduce<IGroupSelectItem[]>((acc, option) => {
		const checkedValues: IGroupSelectItem[] = option.categoryGroups
			.reduce<IGroupSelectItem[]>((acc, categoryGroup) => {
				const checkedValues: Array<IGroupSelectItem> = categoryGroup.items.filter(
					(item) => item.checked,
				);

				return [...acc, ...checkedValues];
			}, []);

		return [...acc, ...checkedValues];
	}, []);
};

export const generateDynamicOptions = (
	options: Array<IGroupSelectOptionsKpi>,
	defaultOptions: Array<IDefaultOptions>,
): IGroupSelectOptions[] => {
	return options.map((option) => {
		return {
			...option,
			categoryGroups: option.categoryGroups.map((categoryGroup) => {
				return {
					...categoryGroup,
					items: categoryGroup.items.map((item) => {
						const checked = defaultOptions.some((element) =>
							element.categoryName
								? element.id === item.id && element.categoryName === categoryGroup.category.name
								: element.id === item.id,
						);

						return {
							...item,
							checked,
						};
					}),
				};
			}),
		};
	});
};

export const generateListOfCheckedOptionsWithCategory = (
	options: Array<IGroupSelectOptions>,
): IGroupSelectOptions[] => {
	return options.map((option) => {
		return {
			...option,
			categoryGroups: option.categoryGroups.map((categoryGroup) => {
				return {
					...categoryGroup,
					items: categoryGroup.items.filter((item) => item.checked),
				};
			}),
		};
	});
};

export const generateDefaultValues = (values: IGroupSelectOptions[]): IDefaultOptions[] => {
	return values.reduce<Array<IDefaultOptions>>((acc, option) => {
		const defaultOption = option.categoryGroups.reduce<Array<IDefaultOptions>>((acc, categoryGroup) => {
			const checkedItems = categoryGroup.items.filter((item) => item.checked);
			const defaultOption = checkedItems.map((item) => ({
				id: item.id,
				categoryName: categoryGroup.category.name,
			}));

			return [...acc, ...defaultOption];
		}, []);

		return [...acc, ...defaultOption];
	}, []);
};

export const sortByChecked = (sortedOptions: IGroupSelectOptions[]): IGroupSelectOptions[] => {
	const sortedOptionsCopy = sortedOptions.map((option: IGroupSelectOptions) => {
		return {
			...option,
			categoryGroups: option.categoryGroups.map((categoryGroup: IGroupSelectCategoryGroup) => {
				const sortedItems = [...categoryGroup.items].sort((a, b) => {
					if (a.checked && !b.checked) return -1;
					if (!a.checked && b.checked) return 1;

					return 0;
				});

				return {
					...categoryGroup,
					items: sortedItems,
				};
			}),
		};
	});

	return sortedOptionsCopy.sort((a, b) => {
		const allOptionsSelectedA = a.categoryGroups.some((categoryGroup) =>
			categoryGroup.items.some((item) => item.checked),
		);
		const allOptionsSelectedB = b.categoryGroups.some((categoryGroup) =>
			categoryGroup.items.some((item) => item.checked),
		);

		if (allOptionsSelectedA && allOptionsSelectedB) {
			return a.technology.name.localeCompare(b.technology.name);
		}
		if (allOptionsSelectedA && !allOptionsSelectedB) {
			return -1;
		}
		if (!allOptionsSelectedA && allOptionsSelectedB) {
			return 1;
		}

		const checkedCountA = a.categoryGroups.reduce((acc, categoryGroup) => {
			const checkedCount = categoryGroup.items.filter((item) => item.checked).length;

			return acc + checkedCount;
		}, 0);
		const checkedCountB = b.categoryGroups.reduce((acc, categoryGroup) => {
			const checkedCount = categoryGroup.items.filter((item) => item.checked).length;

			return acc + checkedCount;
		}, 0);

		return checkedCountB - checkedCountA;
	});
};

export const sortByName = (sortedOptions: IGroupSelectOptions[]): IGroupSelectOptions[] => {
	return sortedOptions.map((option) => {
		return {
			...option,
			categoryGroups: option.categoryGroups
				.map((categoryGroup) => {
					return {
						...categoryGroup,
						items: categoryGroup.items.sort((a, b) => a.title.localeCompare(b.title)),
					};
				})
				// sort by category name
				.sort((a, b) => {
					return a.category.name.localeCompare(b.category.name);
				}),
		};
	});
};

export const filterHiddenOptions = (options: IGroupSelectOptions[]): IGroupSelectOptions[] => {
	return options
		.map((option) => ({
			...option,
			categoryGroups: option.categoryGroups
				.filter((categoryGroup) => !categoryGroup.category.isHidden)
				.map((categoryGroup) => ({
					...categoryGroup,
					items: categoryGroup.items.filter((item) => !item.isHidden),
				})),
		}));
};
